import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { hasSession, useAuthStore } from './authStore.js';

let instance: AxiosInstance | undefined;
let refreshing: Promise<string> | null = null;

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export function api(): AxiosInstance {
  if (instance) return instance;
  instance = axios.create({
    baseURL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (err) => {
      const original = err.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
      const status = err.response?.status as number | undefined;
      if (status === 401 && original && !original._retried) {
        original._retried = true;
        try {
          const newAccess = await getRefresh();
          if (newAccess) {
            original.headers = original.headers ?? {};
            original.headers.Authorization = `Bearer ${newAccess}`;
            return instance!.request(original);
          }
        } catch {
          // fall through to logout
        }
        useAuthStore.getState().clear();
      }
      throw err;
    },
  );

  return instance;
}

/**
 * One refresh at a time — across TABS, not only within this one.
 *
 * The refresh token is a cookie every tab shares, and every refresh rotates it.
 * Two tabs presenting the same cookie at the same instant is, to the server,
 * a stolen token being replayed: it revokes every session that person has, on
 * every device. Tabs are exactly the case that makes it likely — a tab opened
 * from another starts with a copy of the same access token, so they all expire
 * together, and the notifications bell polls in each of them.
 *
 * A Web Lock queues the tabs instead. The second one in line sends the cookie
 * the first one's answer already replaced, so every refresh is an honest one.
 * Where the Locks API is missing (very old browsers), it degrades to the old
 * per-tab behaviour rather than failing.
 */
const REFRESH_LOCK = 'erp-auth-refresh';

export async function getRefresh(): Promise<string> {
  if (refreshing) return refreshing;
  // A refresh renews a session; it cannot make one. With no person and shop in
  // this tab (signed out, or never signed in) the shared cookie would hand back
  // a bare token — see hasSession. The caller signs the tab out instead.
  if (!hasSession(useAuthStore.getState())) throw new Error('no_session');
  const run = async (): Promise<string> => {
    const r = await axios.post<{ accessToken: string; expiresIn: number }>(
      `${baseURL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    useAuthStore.getState().setToken(r.data.accessToken, r.data.expiresIn);
    return r.data.accessToken;
  };
  refreshing = (async () => {
    try {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      return locks ? await locks.request(REFRESH_LOCK, run) : await run();
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}
